import { DatePipe, NgClass } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  signal,
  ViewChild,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { Chart } from "chart.js";
import dayjs from "dayjs";
import { ChartJSLineChart } from "../../../charts/adapters/line-chart";
import { ChartDataGroup } from "../../../charts/models/chart-data-group.model";
import { ChartData } from "../../../charts/models/chart-data.model";
import { ChartComponent } from "../../../components/chart/chart.component";
import { ContentMetricsColor } from "../../../enums/colors";
import {
  MetricsSeriesDataPoint,
  PublicationAggregatedMetrics,
} from "../../domain/models/publication-aggregated-metrics";
import { PublicationTemplateAggregatedMetricsChartSettings } from "./publication-template-aggregated-metrics-chart-settings";

@Component({
  standalone: true,
  selector: "app-publication-template-aggregated-metrics",
  templateUrl: "./publication-template-aggregated-metrics.component.html",
  styleUrl: "./publication-template-aggregated-metrics.component.scss",
  imports: [ChartComponent, NgClass, TranslateModule, DatePipe],
})
export class PublicationTemplateAggregatedMetricsComponent {
  protected readonly metrics = input.required<
    PublicationAggregatedMetrics | undefined
  >();
  protected readonly metricKeys = computed(() =>
    Object.keys(this.metrics()?.totals ?? []),
  );
  protected readonly activeMetric = signal<string | undefined>(undefined);
  @ViewChild("canvas") public canvas?: ElementRef<HTMLCanvasElement>;
  private chart?: Chart;

  constructor() {
    effect(async () => {
      const metric = this.activeMetric();
      if (metric) {
        const metrics = this.metrics()!.series[metric];
        this.createChartInstance(metrics);
      }
    });
  }

  private createChartInstance(metrics: MetricsSeriesDataPoint[]): void {
    const metric = this.activeMetric();
    if (!metric) {
      return;
    }

    if (!this.canvas) {
      setTimeout(() => this.createChartInstance(metrics), 100);
      return;
    }

    this.chart = new ChartJSLineChart().createOrUpdate(
      this.canvas.nativeElement,
      [
        new ChartDataGroup(
          metric,
          ContentMetricsColor,
          Object.entries(metrics).map(
            ([, item]) =>
              new ChartData(dayjs(item.date).format("MMM D"), item.value),
          ),
        ),
      ],
      PublicationTemplateAggregatedMetricsChartSettings,
      true,
    );
  }

  protected onActionKpiSelected(type: string): void {
    if (type === this.activeMetric()) {
      this.activeMetric.set(undefined);
      return;
    }

    this.activeMetric.set(type);
  }
}
